/**
 * F-BULK-IMPORT (2026-05-24) — ImportJobProgressWindow.
 *
 * Reactive view of the current user's bulk-import jobs (via
 * `api.bulkImport.listForUser`). Shows live counters, progress bar,
 * throughput-based ETA, pause/resume/cancel buttons, and a
 * failed-rows CSV export per job.
 *
 * Mounted as a desktop window when the user kicks off a bulk import
 * from BulkImportWizard. Also reachable from the Start menu / desktop
 * icon (added by the host).
 *
 * Design source: future-plans/real-sharepoint/bulk-worker-aca-job.md.
 */
import { useState } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { testIds } from '../../lib/testIds';

const FONT = "var(--xp-font-body)";

const wrapStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: '#ece9d8',
  fontFamily: FONT,
  fontSize: 12,
};

const headerStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderBottom: '1px solid #808080',
  background: '#ece9d8',
};

const listStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: 8,
};

const cardStyle: React.CSSProperties = {
  marginBottom: 10,
  padding: 10,
  background: '#fff',
  borderStyle: 'solid',
  borderWidth: 1,
  borderColor: '#aca899',
};

const barWrapStyle: React.CSSProperties = {
  height: 12,
  background: '#dcdcdc',
  borderStyle: 'inset',
  borderWidth: 1,
  borderColor: '#808080',
  marginTop: 6,
  overflow: 'hidden',
};

const barFillStyle = (pct: number): React.CSSProperties => ({
  width: `${pct}%`,
  height: '100%',
  background: 'linear-gradient(180deg, #4a90e2, #1e6ec1)',
  transition: 'width 240ms ease',
});

interface JobRow {
  _id: Id<'importJobs'>;
  _creationTime: number;
  status: string;
  totalItems: number;
  completedItems: number;
  failedItems: number;
  skippedItems?: number;
  startedAt: string;
  completedAt?: string;
  claimedAt?: string;
  pausedAt?: string;
  cancelledAt?: string;
  lastCallbackAt?: string;
  lastError?: string;
  sourceKind?: string;
  mode?: string;
  dryRun?: boolean;
  matterId: Id<'matters'>;
}

function statusLabel(status: string): string {
  switch (status) {
    case 'queued':
      return 'Queued';
    case 'running':
      return 'Running';
    case 'paused':
      return 'Paused';
    case 'succeeded':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status;
  }
}

function statusColor(status: string): string {
  if (status === 'running' || status === 'queued') return '#1e6ec1';
  if (status === 'paused') return '#a86c00';
  if (status === 'succeeded') return '#1e7a32';
  if (status === 'failed') return '#b00000';
  if (status === 'cancelled') return '#666';
  return '#000';
}

/**
 * ETA estimation. Worker calls progress() roughly every 25 items OR
 * every 30s; we estimate throughput from claimedAt → lastCallbackAt
 * and apply it to the remaining items. Returns a human string like
 * "~3m 20s" or null when we don't have enough data.
 */
function estimateEta(row: JobRow): string | null {
  if (row.status !== 'running') return null;
  if (!row.claimedAt || !row.lastCallbackAt) return null;
  const elapsedMs =
    new Date(row.lastCallbackAt).getTime() - new Date(row.claimedAt).getTime();
  const processed = row.completedItems + row.failedItems + (row.skippedItems ?? 0);
  if (elapsedMs <= 0 || processed <= 0) return null;
  const ratePerMs = processed / elapsedMs;
  const remaining = Math.max(0, row.totalItems - processed);
  if (remaining === 0) return 'almost done';
  if (ratePerMs <= 0) return null;
  const remainingMs = remaining / ratePerMs;
  const seconds = Math.round(remainingMs / 1000);
  if (seconds < 60) return `~${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes < 60) return `~${minutes}m ${secs}s`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `~${hours}h ${mins}m`;
}

function progressPct(row: JobRow): number {
  if (row.totalItems <= 0) return 0;
  const processed = row.completedItems + row.failedItems + (row.skippedItems ?? 0);
  return Math.min(100, Math.round((processed / row.totalItems) * 100));
}

function isTerminal(status: string): boolean {
  return status === 'succeeded' || status === 'failed' || status === 'cancelled';
}

export interface ImportJobProgressWindowProps {
  /** When provided, the window auto-focuses + scrolls this job into view. */
  focusJobId?: Id<'importJobs'>;
}

export function ImportJobProgressWindow({ focusJobId }: ImportJobProgressWindowProps) {
  const jobs = useQuery(api.bulkImport.listForUser) as JobRow[] | undefined;
  return (
    <div style={wrapStyle} data-testid={testIds.bulkImport.progressWindow}>
      <div style={headerStyle}>
        <strong>Bulk imports</strong>{' '}
        <span style={{ color: '#666' }}>
          ({jobs?.length ?? 0} job{jobs?.length === 1 ? '' : 's'})
        </span>
      </div>
      <div style={listStyle}>
        {jobs === undefined && <div style={{ color: '#666' }}>Loading…</div>}
        {jobs && jobs.length === 0 && (
          <div style={{ color: '#666' }}>
            No bulk imports yet. Start one from the SharePoint Explorer.
          </div>
        )}
        {jobs?.map((row) => (
          <JobCard key={row._id} row={row} highlight={row._id === focusJobId} />
        ))}
      </div>
    </div>
  );
}

function JobCard({ row, highlight }: { row: JobRow; highlight: boolean }) {
  const pause = useMutation(api.bulkImport.requestPause);
  const resume = useMutation(api.bulkImport.requestResume);
  const cancel = useMutation(api.bulkImport.requestCancel);
  const kick = useAction(api.bulkImport.kickWorker);
  const failedItems = useQuery(api.bulkImport.failedItems, { jobId: row._id });
  const [showFailed, setShowFailed] = useState(false);

  const pct = progressPct(row);
  const eta = estimateEta(row);
  const terminal = isTerminal(row.status);

  async function handleResume(): Promise<void> {
    const result = await resume({ jobId: row._id });
    if (result?.needsKick) {
      await kick({ jobId: row._id });
    }
  }

  function handleExportCsv(): void {
    if (!failedItems || failedItems.length === 0) return;
    const header = 'externalRef.itemId,name,webUrl,decisionReason\n';
    const lines = failedItems
      .map((it: any) =>
        [
          it.externalRef?.itemId ?? '',
          (it.externalRef?.name ?? '').replace(/[\r\n,]/g, ' '),
          it.externalRef?.webUrl ?? '',
          (it.decisionReason ?? '').replace(/[\r\n,]/g, ' '),
        ].join(','),
      )
      .join('\n');
    const blob = new Blob([header + lines], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-import-${row._id}-failed.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div
      style={{
        ...cardStyle,
        boxShadow: highlight ? '0 0 0 2px var(--xp-selection)' : undefined,
      }}
      data-testid={testIds.bulkImport.progressJobRow(row._id)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <strong
            style={{ color: statusColor(row.status) }}
            data-testid={testIds.bulkImport.progressJobStatus(row._id)}
          >
            {statusLabel(row.status)}
          </strong>
          <span style={{ color: '#666', marginLeft: 8 }}>
            {row.sourceKind ? `${row.sourceKind} • ` : ''}started{' '}
            {new Date(row.startedAt).toLocaleString()}
          </span>
          {row.dryRun && (
            <span style={{ marginLeft: 6, color: '#a86c00' }}>(dry run)</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {row.status === 'running' && (
            <button
              data-testid={testIds.bulkImport.pauseButton(row._id)}
              onClick={() => void pause({ jobId: row._id })}
              style={btnSmall}
            >
              Pause
            </button>
          )}
          {row.status === 'paused' && (
            <button
              data-testid={testIds.bulkImport.resumeButton(row._id)}
              onClick={() => void handleResume()}
              style={btnSmall}
            >
              Resume
            </button>
          )}
          {!terminal && (
            <button
              data-testid={testIds.bulkImport.cancelJobButton(row._id)}
              onClick={() => void cancel({ jobId: row._id })}
              style={btnSmall}
            >
              Cancel
            </button>
          )}
          {row.failedItems > 0 && (
            <button
              data-testid={testIds.bulkImport.exportFailedCsvButton(row._id)}
              onClick={handleExportCsv}
              style={btnSmall}
            >
              Export failed (.csv)
            </button>
          )}
        </div>
      </div>

      <div
        data-testid={testIds.bulkImport.progressJobCounters(row._id)}
        style={{ marginTop: 6 }}
      >
        <span style={{ color: '#1e7a32' }}>{row.completedItems} ok</span>
        {' · '}
        <span style={{ color: '#b00000' }}>{row.failedItems} failed</span>
        {' · '}
        <span style={{ color: '#a86c00' }}>{row.skippedItems ?? 0} skipped</span>
        {' / '}
        <span>{row.totalItems} total</span>
        {eta && (
          <span
            style={{ marginLeft: 12, color: '#1e6ec1' }}
            data-testid={testIds.bulkImport.progressJobETA(row._id)}
          >
            ETA {eta}
          </span>
        )}
      </div>

      <div
        style={barWrapStyle}
        data-testid={testIds.bulkImport.progressJobBar(row._id)}
      >
        <div style={barFillStyle(pct)} />
      </div>

      {row.lastError && (
        <div style={{ marginTop: 6, color: '#b00000', fontSize: 11 }}>
          {row.lastError}
        </div>
      )}

      {row.failedItems > 0 && (
        <details
          open={showFailed}
          onToggle={(e) => setShowFailed((e.target as HTMLDetailsElement).open)}
          style={{ marginTop: 6 }}
        >
          <summary style={{ cursor: 'pointer', color: '#666' }}>
            Failed-rows summary
          </summary>
          <div style={{ marginTop: 4, maxHeight: 120, overflow: 'auto', fontSize: 11 }}>
            {(failedItems ?? []).slice(0, 50).map((it: any) => (
              <div key={it._id}>
                {it.externalRef?.name ?? it.externalRef?.itemId} —{' '}
                <span style={{ color: '#b00000' }}>{it.decisionReason ?? 'unknown'}</span>
              </div>
            ))}
            {failedItems && failedItems.length > 50 && (
              <div style={{ color: '#666' }}>(+{failedItems.length - 50} more — export CSV)</div>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

const btnSmall: React.CSSProperties = {
  minWidth: 64,
  height: 22,
  padding: '0 8px',
  background: '#ece9d8',
  borderStyle: 'solid',
  borderWidth: 2,
  fontFamily: FONT,
  fontSize: 11,
  cursor: 'pointer',
  color: '#000',
};
